import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { UserCalendarStatsService } from './user-calendar-stats.service';
import { UserCalendarStatsView } from 'app/model/userCalendarStats';
import swal, { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';
import { CalendarOptions, EventContentArg, EventInput } from '@fullcalendar/core';
import multiMonthPlugin from "@fullcalendar/multimonth";
import esLocale from "@fullcalendar/core/locales/es";
import { Calendar, CalendarReason, CalendarStatus } from 'app/model/calendar';

@Injectable({
  providedIn: 'root'
})
export class UserCalendarStatsViewService {
  private readonly HOLIDAY_CLASS_NAME: string = 'festivo-bg'

  private readonly EVENT_ACCEPTED_COLOR: string = '#68bc64'
  private readonly EVENT_PENDING_COLOR: string = '#508cdc'
  private readonly EVENT_REJECTED_COLOR: string = '#e85c5c'

  private readonly EVENT_HOLIDAYS_ABREV: string = 'VAC'
  private readonly EVENT_PERSONAL_MATTERS_ABREV: string = 'AP'
  private readonly EVENT_SICK_LEAVE_ABREV: string = 'BAJ'
  private readonly EVENT_PAID_LEAVE_ABREV: string = 'PR'
  private readonly EVENT_REMOTE_WORK_ABREV: string = 'TT'

  /**
   * Constructor
   *
   * @param {UserCalendarStatsService} userCalendarStatsService
   * 
  */
  constructor(
    private userCalendarStatsService: UserCalendarStatsService
  ) { }

  public getUserCalendarStatsView(employeeId: number, year: number): Observable<UserCalendarStatsView> {
    return this.userCalendarStatsService.getUserCalendarStatsViewByEmployeeIdAndYear(employeeId, year)
  }

  public makeCalendarOptions(calendars: Calendar[], holidays: string[], year: number, isMobile: boolean): CalendarOptions {
    return {
      initialView: 'allYear',
      locale: esLocale,
      plugins: [multiMonthPlugin],
      views: {
        allYear: {
          type: 'multiMonth',
          duration: { year: 1 },
          showNonCurrentDates: false,

        }
      },
      buttonText: {
        today: 'Hoy'
      },
      events: this.makeEvents(calendars, holidays, year),
      eventContent: (arg) => this.getCustomEventsContent(arg, isMobile),
      height: 'auto',
      handleWindowResize: true
    }
  }

  public makeEvents(calendars: Calendar[], holidays: string[], year: number): EventInput[] {
    const holidayEvents = this.makeHolidayEvents(holidays)
    const weekendEvents = this.makeWeekendsEvents(year)
    const calendarEvents = this.makeCalendarEvents(calendars)

    return [...holidayEvents, ...weekendEvents, ...calendarEvents]
  }

  private makeHolidayEvents(holidays: string[]): EventInput[] {
    return holidays.map(item => ({
      id: `holiday-${item}`,
      start: new Date(item),
      end: new Date(item),
      allDay: true,
      display: 'background',
      color: 'red',
      className: this.HOLIDAY_CLASS_NAME
    }))
  }

  private makeWeekendsEvents(year: number): EventInput[] {
    const weekendEvents: EventInput[] = []

    const startDate = new Date(year, 0, 1)
    const endDate = new Date(year, 11, 31)

    for (let d = new Date(startDate); d <= endDate; d = new Date(d.setDate(d.getDate() + 1))) {
      const day = d.getDay()

      if (day !== 0 && day !== 6) continue

      weekendEvents.push({
        id: `weekend-${d.toISOString()}`,
        start: new Date(d),
        end: new Date(d),
        allDay: true,
        display: 'background',
        color: 'grey'
      })
    }

    return weekendEvents
  }

  private makeCalendarEvents(calendars: Calendar[]): EventInput[] {
    return calendars.map(item => {
      // La suma del día final se hace porque en Full Calendar el inicio es inclusivo y el final es exclusivo
      const endDate = new Date(item.end_date)
      endDate.setDate(endDate.getDate() + 1)

      return {
        id: `${item.id}`,
        start: new Date(item.start_date),
        end: endDate,
        allDay: true,
        title: item.reason,
        color: this.getEventColorBg(item.status)
      }
    })
  }

  private getEventColorBg(status: string): string {
    const events: Record<CalendarStatus, string> = {
      [CalendarStatus.ACCEPTED]: this.EVENT_ACCEPTED_COLOR,
      [CalendarStatus.PENDING]: this.EVENT_PENDING_COLOR,
      [CalendarStatus.REJECTED]: this.EVENT_REJECTED_COLOR
    }

    return events[status]
  }

  public getCustomEventsContent(arg: EventContentArg, isMobile: boolean) {
    const events: Record<CalendarReason, string> = {
      [CalendarReason.HOLIDAYS]: this.EVENT_HOLIDAYS_ABREV,
      [CalendarReason.PERSONAL_MATTERS]: this.EVENT_PERSONAL_MATTERS_ABREV,
      [CalendarReason.SICK_LEAVE]: this.EVENT_SICK_LEAVE_ABREV,
      [CalendarReason.PAID_LEAVE]: this.EVENT_PAID_LEAVE_ABREV,
      [CalendarReason.REMOTE_WORK]: this.EVENT_REMOTE_WORK_ABREV,
    }

    const textReason = events[arg.event.title] ?? ''

    const fontSize = isMobile ? '0.40em' : '0.75em'

    return {
      html: `<span class="d-flex justify-content-center align-items-center h-100" style="font-size: ${fontSize}">
      <p style="margin-bottom: 2px; margin-top: 2px">${textReason}</p>
      </span>`
    }
  }

  public fireSwalAlert(options: SweetAlertOptions): Promise<SweetAlertResult> {
    return swal.fire(options)
  }
}
